import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Trash2, UserPlus } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChangeEmailCard } from "@/components/ChangeEmailCard";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "" });

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", f); toast.success("User created"); setF({ name: "", email: "", password: "" }); load(); }
    catch (e2) { toast.error(formatErr(e2)); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars)`);
    if (!pw) return;
    try { await api.put(`/admin/users/${u.id}/password`, { new_password: pw }); toast.success("Password updated"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email} and all their data?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const resetAll = async () => {
    if (window.prompt('This deletes ALL groups, sites, entries and every other user. Type DELETE to confirm') !== "DELETE") return;
    try { await api.post("/admin/reset-all"); toast.success("All data deleted. Fresh start!"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <main className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      <Link to="/" data-testid="back-to-dashboard" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" />Back</Link>
      <h1 className="font-head text-4xl sm:text-5xl font-bold tracking-tight">Users</h1>
      <ChangeEmailCard onChanged={load} />
      <form onSubmit={create} data-testid="create-user-form" className="bg-white border rounded-xl p-4 grid sm:grid-cols-4 gap-3">
        <Input data-testid="new-user-name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="new-user-email" type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input data-testid="new-user-password" type="password" required minLength={6} placeholder="Password (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Button data-testid="create-user-btn" className="bg-sky-600 hover:bg-sky-700"><UserPlus className="w-4 h-4 mr-2" />Create user</Button>
      </form>
      <div className="bg-white border rounded-xl divide-y">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{u.name}</p>
              <p className="text-xs font-mono text-slate-500 truncate">{u.email}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-600"}`}>{u.role}</span>
            <Button variant="ghost" size="sm" data-testid={`user-reset-pw-${u.id}`} onClick={() => resetPw(u)}><KeyRound className="w-4 h-4" /></Button>
            {u.role !== "admin" && <Button variant="ghost" size="sm" data-testid={`user-delete-${u.id}`} onClick={() => remove(u)}><Trash2 className="w-4 h-4" /></Button>}
          </div>
        ))}
      </div>
      <div data-testid="danger-zone" className="border border-red-200 bg-red-50 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="font-semibold text-red-800">Delete all data</p>
          <p className="text-xs text-red-700">Removes every group, site, entry and all other users. Only your admin login stays.</p>
        </div>
        <Button data-testid="reset-all-btn" variant="destructive" onClick={resetAll}><Trash2 className="w-4 h-4 mr-2" />Delete all data</Button>
      </div>
    </main>
  );
}
